/**
 * Job Notes tab — detailed troubleshooting, work performed, pump info, and general notes.
 * Especially useful for repair jobs but available on all job types.
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchJobNotes, createJobNote, updateJobNote, deleteJobNote } from '../../../api/jobNotes';
import { Button, LoadingSpinner, EmptyState } from '../../common';

const NOTE_CATEGORIES = [
  { value: 'troubleshooting', label: 'Troubleshooting', color: 'bg-red-100 text-red-700' },
  { value: 'work_performed', label: 'Work Performed', color: 'bg-blue-100 text-blue-700' },
  { value: 'pump_info', label: 'Pump Info / Serial Numbers', color: 'bg-purple-100 text-purple-700' },
  { value: 'inspection', label: 'Inspection', color: 'bg-amber-100 text-amber-700' },
  { value: 'general', label: 'General Note', color: 'bg-gray-100 text-gray-600' },
];

function getCategoryStyle(cat) {
  return NOTE_CATEGORIES.find((c) => c.value === cat) || NOTE_CATEGORIES[4];
}

function fmtDate(d) {
  if (!d) return '';
  try { return format(parseISO(d), 'MMM d, yyyy h:mm a'); } catch { return d; }
}

export default function JobNotesTab({ job }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [newNote, setNewNote] = useState({ category: 'general', title: '', body: '' });
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Filter
  const [filterCategory, setFilterCategory] = useState('');

  const loadNotes = useCallback(async () => {
    try {
      const data = await fetchJobNotes(job.id);
      setNotes(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  async function handleCreate() {
    if (!newNote.body.trim()) { alert('Note body is required.'); return; }
    setSaving(true);
    try {
      await createJobNote({ ...newNote, job: job.id });
      setNewNote({ category: 'general', title: '', body: '' });
      setShowAdd(false);
      loadNotes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(note) {
    setEditingId(note.id);
    setEditForm({ category: note.category, title: note.title || '', body: note.body });
  }

  async function saveEdit() {
    if (!editForm.body.trim()) { alert('Note body is required.'); return; }
    setEditSaving(true);
    try {
      await updateJobNote(editingId, editForm);
      setEditingId(null);
      loadNotes();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update note');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete(noteId) {
    if (!confirm('Delete this note?')) return;
    try {
      await deleteJobNote(noteId);
      if (editingId === noteId) setEditingId(null);
      loadNotes();
    } catch {
      alert('Failed to delete note');
    }
  }

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  const filteredNotes = filterCategory
    ? notes.filter((n) => n.category === filterCategory)
    : notes;

  return (
    <div className="space-y-4">
      {/* Header with add button and filter */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-foreground">Job Notes</h3>
          <span className="text-xs text-gray-400">{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
          <select
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {NOTE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)}>+ Add Note</Button>
      </div>

      {/* Add note form */}
      {showAdd && (
        <div className="bg-white rounded-lg border border-blue-200 px-4 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Category</label>
              <select
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                value={newNote.category}
                onChange={(e) => setNewNote({ ...newNote, category: e.target.value })}
              >
                {NOTE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">Title <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                placeholder="e.g. Initial pump inspection findings"
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Note</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={5}
              placeholder={
                newNote.category === 'troubleshooting'
                  ? 'Describe the issue, symptoms, tests performed, findings...'
                  : newNote.category === 'work_performed'
                  ? 'Describe work completed, parts replaced, adjustments made...'
                  : newNote.category === 'pump_info'
                  ? 'Pump model, serial number, specifications, nameplate data...'
                  : newNote.category === 'inspection'
                  ? 'Inspection findings, measurements, pass/fail results...'
                  : 'Enter detailed notes...'
              }
              value={newNote.body}
              onChange={(e) => setNewNote({ ...newNote, body: e.target.value })}
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} loading={saving}>Save Note</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowAdd(false); setNewNote({ category: 'general', title: '', body: '' }); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {filteredNotes.length === 0 ? (
        !showAdd && (
          <EmptyState
            title="No notes yet"
            description={
              job.job_type === 'repair'
                ? "Add notes to track troubleshooting details, work performed, pump model and serial numbers, and more."
                : "Add notes to keep detailed records for this job."
            }
          />
        )
      ) : (
        <div className="space-y-3">
          {filteredNotes.map((note) => {
            const cat = getCategoryStyle(note.category);
            const isEditing = editingId === note.id;

            return (
              <div key={note.id} className="bg-white rounded-lg border border-gray-200">
                {isEditing ? (
                  /* Edit mode */
                  <div className="px-4 py-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Category</label>
                        <select
                          className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                          value={editForm.category}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        >
                          {NOTE_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 block mb-1">Title</label>
                        <input
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        />
                      </div>
                    </div>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows={5}
                      value={editForm.body}
                      onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} loading={editSaving}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${cat.color}`}>
                          {cat.label}
                        </span>
                        {note.title && (
                          <span className="text-sm font-semibold text-foreground">{note.title}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => startEdit(note)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >Edit</button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >Delete</button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">{note.body}</div>
                    <div className="mt-2 text-xs text-gray-400">
                      {note.author_display && <span>{note.author_display} · </span>}
                      {fmtDate(note.created_at)}
                      {note.updated_at !== note.created_at && (
                        <span className="ml-1">(edited)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
